/**
 * Currency conversion — DISPLAY ONLY (contract §3: "Currency conversion for display →
 * Frontend using FX snapshot from API").
 *
 * The snapshot is `WireFx` (`src/lib/api/wire.ts`): pairs keyed `"<FROM>_<TO>"`, each an
 * explicit `{rate, date}` — not a `{base, [currency]: rate}` map. There is no longer a single
 * "the snapshot's base currency" to fall back to; every conversion names its own target.
 *
 * `fxLookup` returns `null` when no rate covers the pair. What THAT should mean differs by
 * caller, which is why it isn't decided here:
 *   - the comparison workbench's RANKING (`lib/compare.ts`) must EXCLUDE an unconvertible
 *     offer — comparing it on the raw number would silently pick the wrong supplier;
 *   - DISPLAY (this file) shows the original, unconverted `Money` instead of guessing a rate —
 *     never a fabricated number, and never a rate of 1 wearing the wrong currency's label.
 *
 * Always convert using the snapshot the API returned with the payload, never a hardcoded table.
 */

import { fxLookup, type WireFx } from './api/wire'
import type { Currency, Money } from './types'

/** Empty snapshot — every lookup returns `null` except same-currency, so `convert` is a no-op.
 * Use only before the API payload has arrived. */
export const FX_FALLBACK: WireFx = {}

/**
 * Convert money into `to`. Returns the ORIGINAL `Money`, untouched, when the snapshot carries
 * no rate for the pair — display-only honesty, not a ranking decision (see the module header).
 */
export function convert(money: Money, to: Currency, fx: WireFx): Money {
  if (money.currency === to) return money
  const rate = fxLookup(fx, money.currency, to)
  if (rate === null) return money
  return { amount: money.amount * rate, currency: to }
}

/**
 * Bare-number variant for hot loops (comparison matrix). Unlike `convert`, this returns `null`
 * on a missing rate rather than falling back to the original amount — every caller today
 * (`lib/compare.ts`) is ranking, where an unconvertible offer must be excluded, not silently
 * left in the wrong currency's units.
 */
export function amountInBase(amount: number, currency: Currency, to: Currency, fx: WireFx): number | null {
  const rate = fxLookup(fx, currency, to)
  return rate === null ? null : amount * rate
}
